<script lang="ts">
  // *** This file is part of DbGate Premium ***

  import _ from 'lodash';
  import { ChartDefinition, ProcessedChart } from 'dbgate-datalib';
  import WidgetColumnBar from '../widgets/WidgetColumnBar.svelte';
  import WidgetColumnBarItem from '../widgets/WidgetColumnBarItem.svelte';
  import SelectField from '../forms/SelectField.svelte';
  import InlineButton from '../buttons/InlineButton.svelte';
  import ManagerInnerContainer from '../elements/ManagerInnerContainer.svelte';
  import ChartDefinitionEditorSelect from './ChartDefinitionEditorSelect.svelte';
  import { Chart } from 'chart.js/dist';

  export let onClose;
  export let onChange: (changeFunc: (upd: ChartDefinition) => ChartDefinition) => void = null;
  export let chart: ProcessedChart;
  export let managerSize;

  $: definition = chart.definition;
</script>

<WidgetColumnBar>
  <WidgetColumnBarItem title="Settings" name="chartSettings" height="30%" {onClose}>
    <ManagerInnerContainer width={managerSize}>
      <ChartDefinitionEditorSelect
        label="Chart type"
        options={[
          { value: 'bar', label: 'Bar' },
          { value: 'line', label: 'Line' },
          { value: 'timeline', label: 'Timeline' },
          { value: 'pie', label: 'Pie' },
          { value: 'polarArea', label: 'Polar area' },
        ]}
        value={definition.chartType}
        setValue={value => {
          onChange?.(upd => ({
            ...upd,
            chartType: value,
          }));
        }}
        data-testid="ChartDefinitionEditor_chartTypeSelect"
      />

      {#if definition.chartType == 'pie'}
        <ChartDefinitionEditorSelect
          label="Pie ratio limit (for Other group)"
          options={[
            { value: '0', label: 'No limit (show all)' },
            { value: '0.01', label: '1%' },
            { value: '0.02', label: '2%' },
            { value: '0.05', label: '5%' },
            { value: '0.1', label: '10%' },
            { value: '0.2', label: '20%' },
          ]}
          value={definition.pieRatioLimit ?? '0.05'}
          setValue={value => {
            onChange?.(upd => ({
              ...upd,
              pieRatioLimit: value,
            }));
          }}
          data-testid="ChartDefinitionEditor_pieRatioLimitSelect"
        />

        <ChartDefinitionEditorSelect
          label="Pie count limit (for Other group)"
          options={[
            { value: '0', label: 'No limit (show all)' },
            { value: '3', label: '3' },
            { value: '5', label: '5' },
            { value: '10', label: '10' },
            { value: '15', label: '15' },
            { value: '20', label: '20' },
          ]}
          value={definition.pieCountLimit ?? '10'}
          setValue={value => {
            onChange?.(upd => ({
              ...upd,
              pieCountLimit: value,
            }));
          }}
          data-testid="ChartDefinitionEditor_pieCountLimitSelect"
        />
      {/if}

      <ChartDefinitionEditorSelect
        label="X axis field"
        options={chart.availableColumns.map(col => ({
          value: col.field,
          label: `${col.field} (${col.dataType})`,
        }))}
        value={definition.xdef.field}
        setValue={value => {
          onChange?.(upd => ({
            ...upd,
            xdef: {
              ...upd.xdef,
              field: value,
            },
          }));
        }}
        data-testid="ChartDefinitionEditor_xAxisFieldSelect"
      />

      <ChartDefinitionEditorSelect
        label="X axis transform"
        options={[
          { value: 'identity', label: 'Identity (no transform)' },
          { value: 'date:minute', label: 'Date (Minute)' },
          { value: 'date:hour', label: 'Date (Hour)' },
          { value: 'date:day', label: 'Date (Day)' },
          { value: 'date:month', label: 'Date (Month)' },
          { value: 'date:year', label: 'Date (Year)' },
        ]}
        value={definition.xdef.transformFunction}
        setValue={value => {
          onChange?.(upd => ({
            ...upd,
            xdef: {
              ...upd.xdef,
              transformFunction: value,
            },
          }));
        }}
        data-testid="ChartDefinitionEditor_xAxisTransformSelect"
      />

      <ChartDefinitionEditorSelect
        label="X axis sort order"
        options={[
          { value: 'ascKeys', label: 'Ascending Keys' },
          { value: 'descKeys', label: 'Descending Keys' },
          { value: 'ascValues', label: 'Ascending Values' },
          { value: 'descValues', label: 'Descending Values' },
          { value: 'natural', label: 'Natural' },
        ]}
        value={definition.xdef.sortOrder ?? 'ascKeys'}
        setValue={value => {
          onChange?.(upd => ({
            ...upd,
            xdef: {
              ...upd.xdef,
              sortOrder: value,
            },
          }));
        }}
        data-testid="ChartDefinitionEditor_xAxisSortOrderSelect"
      />

      <ChartDefinitionEditorSelect
        label="Grouping field"
        options={[
          {
            value: '',
            label: '(No grouping)',
          },
          ...chart.availableColumns.map(col => ({
            value: col.field,
            label: `${col.field} (${col.dataType})`,
          })),
        ]}
        value={definition.groupingField || ''}
        setValue={value => {
          onChange?.(upd => ({
            ...upd,
            groupingField: value || undefined,
          }));
        }}
        data-testid="ChartDefinitionEditor_groupingFieldSelect"
      />

      {#if definition.groupingField}
        <ChartDefinitionEditorSelect
          label="Grouping field transform"
          options={[
            { value: 'identity', label: 'Identity (no transform)' },
            { value: 'date:minute', label: 'Date (Minute)' },
            { value: 'date:hour', label: 'Date (Hour)' },
            { value: 'date:day', label: 'Date (Day)' },
            { value: 'date:month', label: 'Date (Month)' },
            { value: 'date:year', label: 'Date (Year)' },
          ]}
          value={definition.groupTransformFunction || 'identity'}
          setValue={value => {
            onChange?.(upd => ({
              ...upd,
              groupTransformFunction: value,
            }));
          }}
          data-testid="ChartDefinitionEditor_groupTransformSelect"
        />
      {/if}

      {#each definition.ydefs as ydef, index}
        <ChartDefinitionEditorSelect
          label={`Y axis field ${index + 1}`}
          options={[
            {
              value: '__count',
              label: 'COUNT (number of rows)',
            },
            ...chart.availableColumns.map(col => ({
              value: col.field,
              label: `${col.field} (${col.dataType})`,
            })),
          ]}
          value={ydef.field}
          setValue={value => {
            onChange?.(upd => {
              const newYdefs = [...upd.ydefs];
              newYdefs[index] = {
                ...newYdefs[index],
                field: value,
                aggregateFunction: value == '__count' ? 'count' : 'sum',
                // title: _.startCase(value),
              };
              return {
                ...upd,
                ydefs: newYdefs,
              };
            });
          }}
          data-testid={`ChartDefinitionEditor_yAxisFieldSelect_${index}`}
        />

        {#if ydef.field != '__count'}
          <ChartDefinitionEditorSelect
            label={`Y axis field ${index + 1} aggregate`}
            options={[
              { value: 'sum', label: 'Sum' },
              { value: 'avg', label: 'Average' },
              { value: 'min', label: 'Minimum' },
              { value: 'max', label: 'Maximum' },
              { value: 'first', label: 'First' },
              { value: 'last', label: 'Last' },
              // { value: 'count', label: 'Count' },
            ]}
            value={ydef.aggregateFunction}
            setValue={value => {
              onChange?.(upd => {
                const newYdefs = [...upd.ydefs];
                newYdefs[index] = {
                  ...newYdefs[index],
                  aggregateFunction: value,
                };
                return {
                  ...upd,
                  ydefs: newYdefs,
                };
              });
            }}
            data-testid={`ChartDefinitionEditor_yAxisFieldAggregateSelect_${index}`}
          />
        {/if}

        <div class="m-3">
          <InlineButton
            on:click={() => {
              onChange?.(upd => ({
                ...upd,
                ydefs: upd.ydefs.filter((_, i) => i !== index),
              }));
            }}
          >
            Remove {ydef.field == '__count' ? 'COUNT (number of rows)' : ydef.field}
          </InlineButton>
        </div>
      {/each}

      <div class="m-3">
        <InlineButton
          on:click={() => {
            onChange?.(upd => ({
              ...upd,
              ydefs: [
                ...upd.ydefs,
                {
                  field: chart.availableColumns[0]?.field,
                  aggregateFunction: 'sum',
                },
              ],
            }));
          }}
        >
          Add Y Axis
        </InlineButton>
      </div>
    </ManagerInnerContainer>
  </WidgetColumnBarItem>
</WidgetColumnBar>
